/**
 * SoundManager - Web Audio API for layered split-flap sounds
 * Free sound effects from Freesound.org
 */
class SoundManager {
  constructor() {
    this.audioContext = null;
    this.sounds = {
      clack: null,
      flutter: null,
      thud: null
    };
    this.masterVolume = 0.5;
    this.enabled = false;
    this.layerSettings = {
      clack: { enabled: true, volume: 1.0, pitch: 1.0 },
      flutter: { enabled: true, volume: 0.8, pitch: 1.0 },
      thud: { enabled: true, volume: 0.9, pitch: 1.0 }
    };
  }

  async init() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Generate simple procedural sounds as fallback
      // In production, these would load from actual audio files
      this.sounds.clack = this.generateClack();
      this.sounds.flutter = this.generateFlutter();
      this.sounds.thud = this.generateThud();
      
      this.enabled = true;
      console.log('SoundManager initialized');
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  }

  generateClack() {
    // Sharp mechanical CLACK - metal hitting metal
    const duration = 0.08;
    const sampleRate = this.audioContext.sampleRate;
    const buffer = this.audioContext.createBuffer(1, duration * sampleRate, sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      const envelope = Math.exp(-t * 80);
      
      // Metal impact - mix of high freq click + low thud
      const highClick = Math.sin(2 * Math.PI * 2400 * t) * envelope;
      const midClick = Math.sin(2 * Math.PI * 800 * t) * envelope * 0.6;
      const lowThud = Math.sin(2 * Math.PI * 120 * t) * envelope * 0.4;
      const noise = (Math.random() * 2 - 1) * 0.15 * envelope;
      
      data[i] = (highClick + midClick + lowThud + noise) * 0.5;
    }
    
    return buffer;
  }

  generateFlutter() {
    // Mechanical flutter - card vibration
    const duration = 0.12;
    const sampleRate = this.audioContext.sampleRate;
    const buffer = this.audioContext.createBuffer(1, duration * sampleRate, sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      const envelope = Math.sin(t * Math.PI / duration) * Math.exp(-t * 8);
      
      // Rapid vibration - mechanical rattle
      const flutter1 = Math.sin(2 * Math.PI * 180 * t) * envelope;
      const flutter2 = Math.sin(2 * Math.PI * 340 * t) * envelope * 0.5;
      const rattle = Math.sin(2 * Math.PI * 95 * t) * envelope * 0.3;
      const noise = (Math.random() * 2 - 1) * 0.12 * envelope;
      
      data[i] = (flutter1 + flutter2 + rattle + noise) * 0.35;
    }
    
    return buffer;
  }

  generateThud() {
    // Deep mechanical THUD - card settling
    const duration = 0.15;
    const sampleRate = this.audioContext.sampleRate;
    const buffer = this.audioContext.createBuffer(1, duration * sampleRate, sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      const envelope = Math.exp(-t * 25);
      
      // Deep thud - mostly low frequencies
      const deepThud = Math.sin(2 * Math.PI * 60 * t) * envelope;
      const midThud = Math.sin(2 * Math.PI * 140 * t) * envelope * 0.4;
      const impact = Math.sin(2 * Math.PI * 280 * t) * envelope * 0.2;
      const noise = (Math.random() * 2 - 1) * 0.08 * envelope;
      
      data[i] = (deepThud + midThud + impact + noise) * 0.6;
    }
    
    return buffer;
  }

  playSound(soundName, volumeVariance = 0, pitchVariance = 0) {
    if (!this.enabled || !this.audioContext || !this.sounds[soundName]) {
      return;
    }

    const settings = this.layerSettings[soundName];
    if (!settings.enabled) {
      return;
    }

    try {
      const source = this.audioContext.createBufferSource();
      source.buffer = this.sounds[soundName];
      
      // Apply pitch variation
      const pitchFactor = 1 + (Math.random() * 2 - 1) * pitchVariance;
      source.playbackRate.value = settings.pitch * pitchFactor;
      
      // Apply volume variation
      const gainNode = this.audioContext.createGain();
      const volumeFactor = 1 + (Math.random() * 2 - 1) * volumeVariance;
      gainNode.gain.value = this.masterVolume * settings.volume * volumeFactor;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      source.start();
    } catch (error) {
      console.error('Failed to play sound:', error);
    }
  }

  playClack() {
    this.playSound('clack', 0.1, 0.05);
  }

  playFlutter() {
    this.playSound('flutter', 0.1, 0.05);
  }

  playThud() {
    this.playSound('thud', 0.1, 0.05);
  }

  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
  }

  setLayerEnabled(layer, enabled) {
    if (this.layerSettings[layer]) {
      this.layerSettings[layer].enabled = enabled;
    }
  }

  setLayerVolume(layer, volume) {
    if (this.layerSettings[layer]) {
      this.layerSettings[layer].volume = Math.max(0, Math.min(1, volume));
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
  }
}

export default SoundManager;

